import {
  useGetDashboardQuery,
  useSaveDashboardMutation,
  useGetWidgetDataQuery,
  useGetWidgetTemplatesQuery,
  useCreateWidgetTemplateMutation,
  useUpdateWidgetTemplateMutation,
  useDeleteWidgetTemplateMutation,
  useClearWidgetCacheMutation,
} from "../store/api/mlbApi";

// Hook to get user dashboard
export const useDashboard = (dashboardType) => {
  const { data, error, isLoading, refetch } =
    useGetDashboardQuery(dashboardType);

  return {
    dashboard: data?.dashboard || null,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

// Hook to save dashboard
export const useSaveDashboard = (dashboardType) => {
  const [saveDashboard, { isLoading, error }] = useSaveDashboardMutation();
  const { data: currentDashboard, refetch } =
    useGetDashboardQuery(dashboardType);

  const saveUserDashboard = async (layout, gridConfig) => {
    try {
      const result = await saveDashboard({
        dashboardType,
        layout,
        gridConfig,
      }).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to save dashboard",
      };
    }
  };

  const getCurrentDashboard = async () => {
    try {
      const result = await refetch();
      if (result.data) {
        return { success: true, data: result.data.dashboard };
      } else {
        return {
          success: false,
          error: "Failed to fetch dashboard",
        };
      }
    } catch (err) {
      return {
        success: false,
        error: err?.message || "Failed to fetch dashboard",
      };
    }
  };

  return {
    saveUserDashboard,
    getCurrentDashboard,
    currentDashboard: currentDashboard?.dashboard,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

// Hook to get widget data
export const useWidgetData = (
  dataSource,
  timeRange = "month",
  forceRefresh = false
) => {
  const { data, error, isLoading, refetch } = useGetWidgetDataQuery({
    dataSource,
    timeRange,
    forceRefresh,
  });

  return {
    data: data?.data || null,
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

// Hook to get widget templates
export const useWidgetTemplates = (dashboardType, category) => {
  const { data, error, isLoading, refetch } = useGetWidgetTemplatesQuery({
    dashboardType,
    category,
  });

  return {
    templates: data?.templates || [],
    error: error?.data?.error || error?.message,
    isLoading,
    refetch,
  };
};

// Hook to create widget template (admin only)
export const useCreateWidgetTemplate = () => {
  const [createTemplate, { isLoading, error }] =
    useCreateWidgetTemplateMutation();

  const createWidgetTemplate = async (template) => {
    try {
      const result = await createTemplate(template).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to create template",
      };
    }
  };

  return {
    createWidgetTemplate,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

// Hook to update widget template (admin only)
export const useUpdateWidgetTemplate = () => {
  const [updateTemplate, { isLoading, error }] =
    useUpdateWidgetTemplateMutation();

  const updateWidgetTemplate = async (templateId, template) => {
    try {
      const result = await updateTemplate({ templateId, ...template }).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to update template",
      };
    }
  };

  return {
    updateWidgetTemplate,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

// Hook to delete widget template (admin only)
export const useDeleteWidgetTemplate = () => {
  const [deleteTemplate, { isLoading, error }] =
    useDeleteWidgetTemplateMutation();

  const deleteWidgetTemplate = async (templateId) => {
    try {
      const result = await deleteTemplate(templateId).unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to delete template",
      };
    }
  };

  return {
    deleteWidgetTemplate,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};

// Hook to clear widget cache (admin only)
export const useClearWidgetCache = () => {
  const [clearCache, { isLoading, error }] = useClearWidgetCacheMutation();

  const clearWidgetCache = async () => {
    try {
      const result = await clearCache().unwrap();
      return { success: true, data: result };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.error || err?.message || "Failed to clear cache",
      };
    }
  };

  return {
    clearWidgetCache,
    isLoading,
    error: error?.data?.error || error?.message,
  };
};
